import { createServerFn } from "@tanstack/react-start";
import type {
  GeneratedBoxArticle,
  HomeBox,
  HomeBoxArticle,
  HomeBoxArticleSummary,
} from "./home-box.server";

/** Public: the topic boxes shown on the homepage. */
export const fetchHomeBoxes = createServerFn({ method: "GET" }).handler(async () => {
  const { listBoxes, listBoxArticleSummaries } = await import("./home-box.server");
  const [boxes, articles] = await Promise.all([listBoxes(), listBoxArticleSummaries()]);
  return {
    boxes: boxes as HomeBox[],
    counts: articles.reduce<Record<string, number>>((acc, a) => {
      acc[a.box_id] = (acc[a.box_id] ?? 0) + 1;
      return acc;
    }, {}),
  };
});

/** Public: every topic box with its published articles (for the /topics index). */
export const fetchTopicsIndex = createServerFn({ method: "GET" }).handler(async () => {
  const { listBoxes, listBoxArticleSummaries } = await import("./home-box.server");
  const [boxes, articles] = await Promise.all([listBoxes(), listBoxArticleSummaries()]);
  return {
    topics: (boxes as HomeBox[]).map((b) => ({
      id: b.id,
      title: b.title,
      slug: b.slug,
      blurb: b.blurb,
      articles: articles
        .filter((a) => a.box_id === b.id)
        .map((a) => ({ id: a.id, title: a.title, slug: a.slug, excerpt: a.excerpt })),
    })),
  };
});

/** Public: one box with its published articles, plus sibling boxes for cross-links. */
export const fetchBoxWithArticles = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }) => {
    const { listBoxes, listBoxArticleSummaries } = await import("./home-box.server");
    const boxes = await listBoxes();
    const otherBoxes = boxes
      .filter((b) => b.slug !== data.slug)
      .map((b) => ({ title: b.title, slug: b.slug }));
    const box = boxes.find((b) => b.slug === data.slug) ?? null;
    if (!box) return { box: null, articles: [] as HomeBoxArticleSummary[], otherBoxes };
    const articles = await listBoxArticleSummaries(box.id);
    return { box, articles, otherBoxes };
  });


/** Public: lightweight list of all topic boxes (title + slug) for navigation. */
export const fetchTopicNav = createServerFn({ method: "GET" }).handler(async () => {
  const { listBoxes } = await import("./home-box.server");
  const boxes = await listBoxes();
  return { boxes: boxes.map((b) => ({ title: b.title, slug: b.slug })) };
});


/** Public: a single article inside a box. */
export const fetchBoxArticle = createServerFn({ method: "GET" })
  .inputValidator((data: { boxSlug: string; articleSlug: string }) => data)
  .handler(async ({ data }) => {
    const { getBoxArticle } = await import("./home-box.server");
    return getBoxArticle(data.boxSlug, data.articleSlug);
  });

/* ----------------------------- admin ----------------------------- */

export const adminHomeBoxes = createServerFn({ method: "GET" }).handler(async () => {
  const { requireAdmin } = await import("./admin-auth.server");
  await requireAdmin();
  const { listBoxes, listBoxArticles } = await import("./home-box.server");
  const [boxes, articles] = await Promise.all([listBoxes(), listBoxArticles()]);
  return { boxes: boxes as HomeBox[], articles: articles as HomeBoxArticle[] };
});

export const adminCreateBox = createServerFn({ method: "POST" })
  .inputValidator((data: { title: string; blurb: string; icon: string }) => data)
  .handler(async ({ data }) => {
    const { requireAdmin } = await import("./admin-auth.server");
    await requireAdmin();
    const { createBox } = await import("./home-box.server");
    return createBox(data);
  });

export const adminUpdateBox = createServerFn({ method: "POST" })
  .inputValidator((data: { id: string; title?: string; blurb?: string; icon?: string }) => data)
  .handler(async ({ data }) => {
    const { requireAdmin } = await import("./admin-auth.server");
    await requireAdmin();
    const { updateBox } = await import("./home-box.server");
    await updateBox(data);
    return { ok: true as const };
  });

export const adminDeleteBox = createServerFn({ method: "POST" })
  .inputValidator((data: { id: string }) => data)
  .handler(async ({ data }) => {
    const { requireAdmin } = await import("./admin-auth.server");
    await requireAdmin();
    const { deleteBox } = await import("./home-box.server");
    await deleteBox(data.id);
    return { ok: true as const };
  });

export const adminGenerateBoxArticle = createServerFn({ method: "POST" })
  .inputValidator((data: { boxId: string; prompt: string }) => data)
  .handler(async ({ data }): Promise<GeneratedBoxArticle> => {
    const { requireAdmin } = await import("./admin-auth.server");
    await requireAdmin();
    const { generateBoxArticle } = await import("./home-box.server");
    if (!data.prompt?.trim()) throw new Error("Paste your article or notes first.");
    return generateBoxArticle(data.boxId, data.prompt);
  });

export const adminSaveBoxArticle = createServerFn({ method: "POST" })
  .inputValidator(
    (data: {
      id?: string;
      boxId: string;
      title: string;
      slug?: string;
      excerpt: string;
      heroImageUrl?: string | null;
      heroImageAlt?: string;
      bodyHtml: string;
      published: boolean;
    }) => data,
  )
  .handler(async ({ data }) => {
    const { requireAdmin } = await import("./admin-auth.server");
    await requireAdmin();
    const { saveBoxArticle } = await import("./home-box.server");
    if (!data.bodyHtml?.trim()) throw new Error("Article body is required.");
    return { ok: true as const, article: (await saveBoxArticle(data)) as HomeBoxArticle };
  });

export const adminDeleteBoxArticle = createServerFn({ method: "POST" })
  .inputValidator((data: { id: string }) => data)
  .handler(async ({ data }) => {
    const { requireAdmin } = await import("./admin-auth.server");
    await requireAdmin();
    const { deleteBoxArticle } = await import("./home-box.server");
    await deleteBoxArticle(data.id);
    return { ok: true as const };
  });
